/* ---------------------------------------------------------------------------
   Where this device's preferences live, and the one narrow read a second
   document needs.

   The register's store owns them — see PERSIST_KEYS in src/core/store.ts, which
   reads its key from here. This file exists so the Defect Inspector can read one
   preference WITHOUT importing that store: store.ts is the register entire, its
   actions, its API client and every screen's state, and inspector.html is a
   separate document whose whole premise is that it does not carry any of that.

   So the key is named once, here, and both documents read it from the same
   place. The alternative was a copy of the string in two entry points, which is
   the kind of thing that survives every review and then breaks silently on the
   day one of them is renamed.
   --------------------------------------------------------------------------- */

import type { Theme } from '../data/types.ts';

/** The one localStorage key this application writes. */
export const STORAGE_KEY = 'bridge-monitor/v1';

/**
 * The theme this device last chose, or null when it has not chosen one.
 *
 * Null rather than a default. The caller knows its own default and the register's
 * is recorded in exactly one place — `initial()` in store.ts — so guessing
 * 'light' here would make this a second place for it to be written down.
 *
 * Reads the persisted payload directly rather than through the store's `load`,
 * which prunes a dozen keys against register data this page has not fetched.
 * Anything unreadable is null: a preference is a courtesy, and a page that threw
 * because a payload was from an older build would be a worse answer.
 */
export function storedTheme(): Theme | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const theme = (parsed as Record<string, unknown>)['theme'];
    return theme === 'light' || theme === 'dark' ? theme : null;
  } catch {
    /* Private mode, storage disabled, or a payload this build cannot parse. */
    return null;
  }
}
